import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, ChevronLeft, ChevronRight, GripVertical, FileText, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { assetUrl, uploadFiles, errMsg } from "@/lib/api";
import { useDragReorder, move } from "@/admin/dnd";

const useUploader = (onDone) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      onDone(await uploadFiles(files));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return [busy, upload];
};

export const ImageField = ({ value, onChange, testId, accept = "image/*" }) => {
  const input = useRef(null);
  const [busy, upload] = useUploader((docs) => onChange(docs[0].url));
  const isPdf = accept.includes("pdf");
  return (
    <div className="space-y-2" data-testid={testId}>
      {value && (isPdf ? (
        <a href={assetUrl(value)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-brand underline"><FileText className="h-4 w-4" />Current PDF</a>
      ) : (
        <img src={assetUrl(value)} alt="" className="max-h-40 w-auto rounded border bg-muted" data-testid={`${testId}-preview`} />
      ))}
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload or paste a link" className="text-xs" data-testid={`${testId}-url`} />
        <input ref={input} type="file" accept={accept} hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`${testId}-file`} />
        <Button type="button" variant="outline" size="sm" onClick={() => input.current.click()} disabled={busy} data-testid={`${testId}-upload`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        </Button>
        {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testId}-clear`}><X className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

export const ImagesField = ({ value = [], onChange, testId }) => {
  const input = useRef(null);
  const [busy, upload] = useUploader((docs) => onChange([...value, ...docs.map((d) => d.url)]));
  const drag = useDragReorder(value, onChange);
  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {value.map((src, i) => (
          <div key={`${src}-${i}`} {...drag(i)} className="group relative rounded-lg border bg-white p-1.5 cursor-move" data-testid={`${testId}-item-${i}`}>
            <img src={assetUrl(src)} alt="" className="h-24 w-full object-contain pointer-events-none" />
            <GripVertical className="absolute top-1 left-1 h-4 w-4 text-charcoal/40" />
            <div className="mt-1 flex justify-between">
              <button type="button" onClick={() => onChange(move(value, i, i - 1))} className="p-1 hover:text-brand" aria-label="Move left" data-testid={`${testId}-left-${i}`}><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1 text-destructive" aria-label="Remove" data-testid={`${testId}-remove-${i}`}><X className="h-4 w-4" /></button>
              <button type="button" onClick={() => onChange(move(value, i, i + 1))} className="p-1 hover:text-brand" aria-label="Move right" data-testid={`${testId}-right-${i}`}><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => input.current.click()} disabled={busy} className="h-[132px] rounded-lg border-2 border-dashed border-brass/60 flex flex-col items-center justify-center gap-1 text-xs text-charcoal/60 hover:border-brand hover:text-brand" data-testid={`${testId}-add`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}Add images
        </button>
      </div>
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`${testId}-file`} />
      <p className="mt-2 text-xs text-muted-foreground">Drag to reorder or use the arrows.</p>
    </div>
  );
};

const SWATCHES = [["Charcoal", "#1F1C1B"], ["Black", "#000000"], ["Primary", "#9D231E"], ["Gold", "#B08D57"], ["White", "#FFFFFF"]];

export const ColorField = ({ value, onChange, testId }) => (
  <div className="flex flex-wrap items-center gap-3" data-testid={testId}>
    <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#1F1C1B"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-10 w-12 rounded border cursor-pointer" data-testid={`${testId}-picker`} />
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="w-28 font-mono text-xs" data-testid={`${testId}-hex`} />
    {SWATCHES.map(([n, c]) => (
      <button key={n} type="button" title={n} onClick={() => onChange(c)} className="h-8 w-8 rounded-full border-2 border-white ring-1 ring-border shadow" style={{ background: c }} data-testid={`${testId}-swatch-${n.toLowerCase()}`} />
    ))}
  </div>
);

export const SliderField = ({ value, onChange, min = 0, max = 1, step = 0.05, suffix = "", testId }) => (
  <div className="flex items-center gap-4" data-testid={testId}>
    <Slider value={[Number(value)]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1 max-w-sm" />
    <span className="w-14 text-sm font-mono" data-testid={`${testId}-value`}>{Number(value)}{suffix}</span>
  </div>
);
